<ng-container *ngIf="state$ | async as state">
  <div class="card mt-2">
    <div class="card-body">
      <h2 class="mb-3">Employee transitions</h2>
      <p>Find an employee's transition, inspect requirements and recorded advancement errors, and help complete its next step. Access follows the same campus visibility as the completed KRA report.</p>
      <p class="alert alert-danger" *ngIf="state.error" role="alert">{{state.error}}</p>
      <p class="alert alert-info" *ngIf="state.notice" role="status">{{state.notice}}</p>
      <div class="mb-3">
        <div class="row">
          <div class="col-2 d-flex align-items-center">
            <label class="h5" for="ct-employee-filter">Employee:</label>
          </div>
          <div class="col-10">
            <div class="ml-2 w-100 mb-2">
              <p-autoComplete inputId="ct-employee-filter" [style]="{'width': '100%'}" [inputStyle]="{'width': '100%'}"
                [(ngModel)]="selectedUserEmployee" [suggestions]="userEmployeeList" (completeMethod)="getStaffList($event)"
                field="name" dataKey="id" [ngModelOptions]="{standalone: true}" [minLength]="1" [autoHighlight]="true"
                [forceSelection]="true" [showClear]="true" [disabled]="state.saving"
                (onSelect)="employeeChanged($event.value)" (onClear)="employeeChanged()"></p-autoComplete>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-2 d-flex align-items-center">
            <label class="h5" for="ct-campus-filter">Campus:</label>
          </div>
          <div class="col-10">
            <p-select inputId="ct-campus-filter" styleClass="app-select ml-2 w-100 mb-2"
              [options]="state.lookups.campuses" optionLabel="name" optionValue="id" [(ngModel)]="filter.campusID"
              [ngModelOptions]="{standalone: true}" [showClear]="true" placeholder="All accessible campuses"
              [disabled]="state.saving" (ngModelChange)="load()"></p-select>
          </div>
        </div>
        <div class="row">
          <div class="col-2 d-flex align-items-center">
            <label class="h5" for="ct-process-filter">Credential transition:</label>
          </div>
          <div class="col-10">
            <p-select inputId="ct-process-filter" styleClass="app-select ml-2 w-100 mb-2"
              [options]="state.lookups.processes" optionLabel="name" optionValue="id" [(ngModel)]="filter.processID"
              [ngModelOptions]="{standalone: true}" [showClear]="true" placeholder="All processes"
              [disabled]="state.saving" (ngModelChange)="load()"></p-select>
          </div>
        </div>
        <div class="row mb-1">
          <div class="col-2 d-flex align-items-center">
            <h5>Include inactive or separated employees:</h5>
          </div>
          <div class="col-10">
            <p-selectButton styleClass="w-100 ml-2" [options]="includeInactiveOptions" [(ngModel)]="filter.includeInactive"
              [ngModelOptions]="{standalone: true}" [allowEmpty]="false" [disabled]="state.saving"
              (ngModelChange)="load()"></p-selectButton>
          </div>
        </div>
      </div>
      <p-table #myTable [value]="state.list?.result" [responsive]="true" [paginator]="true" [lazy]="true"
        [rows]="filter.pageSize" [first]="filter.page * filter.pageSize" [totalRecords]="state.list?.totalRecords || 0"
        sortField="createdOn" [sortOrder]="-1" [loading]="state.loadingList"
        (onLazyLoad)="reloadFilter($event)">
        <ng-template pTemplate="header">
          <tr>
            <th width="100px"></th>
            <th pSortableColumn="employeeName">Employee <p-sortIcon field="employeeName"></p-sortIcon></th>
            <th pSortableColumn="campus">Campus <p-sortIcon field="campus"></p-sortIcon></th>
            <th pSortableColumn="processName">Process / version <p-sortIcon field="processName"></p-sortIcon></th>
            <th pSortableColumn="createdOn">Started <p-sortIcon field="createdOn"></p-sortIcon></th>
            <th pSortableColumn="stage">Status <p-sortIcon field="stage"></p-sortIcon></th>
            <th pSortableColumn="lastError">Last advancement error <p-sortIcon field="lastError"></p-sortIcon></th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-transition>
          <tr>
            <td>
              <button class="btn btn-secondary mr-2" title="View transition" aria-label="View transition"
                [disabled]="state.saving || state.loadingDetail" (click)="open(transition.id)">
                <i class="fas fa-file-alt"></i>
              </button>
            </td>
            <td>
              <span class="ui-column-title">Employee</span>
              {{transition.employeeName}} <span *ngIf="transition.inactive" class="badge badge-secondary">Inactive</span>
            </td>
            <td>
              <span class="ui-column-title">Campus</span>
              {{transition.campus || 'Unassigned'}}
            </td>
            <td>
              <span class="ui-column-title">Process / version</span>
              {{transition.processName}} / {{transition.versionNumber}}
            </td>
            <td>
              <span class="ui-column-title">Started</span>
              {{transition.createdOn | date:'MM/dd/yyyy'}}
            </td>
            <td>
              <span class="ui-column-title">Status</span>
              {{transition.stage}}
            </td>
            <td>
              <span class="ui-column-title">Last advancement error</span>
              {{transition.lastError || 'None'}}
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr><td colspan="7">No transitions match these filters.</td></tr>
        </ng-template>
        <ng-template pTemplate="paginatorleft" let-page>
          Showing {{page.totalRecords ? page.first + 1 : 0}} - {{(page.first + page.rows) > page.totalRecords ? page.totalRecords : page.first + page.rows}} of {{page.totalRecords}} entries
        </ng-template>
        <ng-template pTemplate="paginatorright">
          <div class="d-flex align-items-center">
            <span class="mr-2">Rows per page:</span>
            <p-select [options]="[20, 50, 100]" [ngModel]="myTable.rows" [ngModelOptions]="{standalone: true}"
              [disabled]="state.saving"
              (onChange)="reloadFilter({first: 0, rows: $event.value, sortField: filter.sortField, sortOrder: filter.sortOrder})"></p-select>
          </div>
        </ng-template>
      </p-table>
      <i *ngIf="state.loadingDetail" class="material-icons rotate text-muted" aria-label="Loading transition details">loop</i>
    </div>
  </div>
  <section class="card mt-3" *ngIf="detail">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center">
        <h3 class="h4">{{detail.employeeName}} — {{detail.status.processName}}</h3>
        <button class="btn btn-secondary" [disabled]="state.saving" (click)="close()">Close details</button>
      </div>
      <p>{{detail.campus || 'Unassigned campus'}} · Started {{detail.createdOn | date:'short'}} · Assigned version {{detail.versionNumber}}</p>
      <p *ngIf="detail.completedOn">Completed {{detail.completedOn | date:'short'}}</p>
      <p class="alert alert-warning" *ngIf="detail.status.paused">This process is paused. Offer generation and sending are blocked.</p>
      <p class="alert alert-danger" *ngIf="detail.lastError"><strong>Last advancement error:</strong> {{detail.lastError}}</p>
      <p class="alert alert-info" *ngIf="detail.status.message && detail.status.message !== detail.lastError">{{detail.status.message}}</p>
      <dl class="row">
        <dt class="col-sm-4">Target credential</dt>
        <dd class="col-sm-8">{{detail.status.targetCredential}}</dd>
        <dt class="col-sm-4">Exam date</dt>
        <dd class="col-sm-8">{{detail.status.examDate ? (detail.status.examDate | date:'shortDate') : 'Not entered'}}{{detail.status.requireExamDate ? ' (required)' : ' (optional)'}}</dd>
        <dt class="col-sm-4">Self review</dt>
        <dd class="col-sm-8">{{detail.status.requireKra ? detail.status.selfReviewStatus : 'Not required'}}</dd>
        <dt class="col-sm-4" *ngIf="detail.status.requireKra">Self review valid through</dt>
        <dd class="col-sm-8" *ngIf="detail.status.requireKra">{{detail.status.selfReviewExpiresOn ? (detail.status.selfReviewExpiresOn | date:'shortDate') : 'Awaiting submission'}}</dd>
        <dt class="col-sm-4">Oversight review</dt>
        <dd class="col-sm-8">{{detail.status.requireKra ? detail.status.oversightReviewStatus : 'Not required'}}</dd>
        <dt class="col-sm-4" *ngIf="detail.status.requireKra">Oversight review valid through</dt>
        <dd class="col-sm-8" *ngIf="detail.status.requireKra">{{detail.status.oversightReviewExpiresOn ? (detail.status.oversightReviewExpiresOn | date:'shortDate') : 'Awaiting submission'}}</dd>
        <dt class="col-sm-4">Credential verification</dt>
        <dd class="col-sm-8">{{detail.status.credentialStatus}} (required)</dd>
        <dt class="col-sm-4">Credential upload</dt>
        <dd class="col-sm-8">{{detail.status.requireCredentialUpload ? 'Required' : 'Optional'}}</dd>
        <dt class="col-sm-4">Offer/change</dt>
        <dd class="col-sm-8">{{detail.status.offerStatus}}</dd>
      </dl>
      <p>Complete credential uploads, verification, and employee reviews through their existing screens.</p>
      <a class="btn btn-outline-secondary mb-3 align-self-start" [routerLink]="['/employee-review/list', detail.userID]">Employee reviews</a>
      <form *ngIf="detail.canManage" (ngSubmit)="save()">
        <fieldset [disabled]="state.saving">
          <div class="form-group">
            <label for="ct-detail-exam">Exam date</label>
            <input id="ct-detail-exam" type="date" class="form-control" name="examDate" [(ngModel)]="examDate" min="2000-01-01" max="2099-12-31">
          </div>
          <div class="form-group" *ngIf="detail.canChangeVersion">
            <label for="ct-detail-version">Assigned process version</label>
            <select id="ct-detail-version" class="form-control" name="version" [(ngModel)]="versionID">
              <option *ngFor="let version of detail.versions" [ngValue]="version.id">Version {{version.versionNumber}} — {{version.sourceCredential}} → {{version.targetCredential}}</option>
            </select>
            <p class="mt-2" *ngIf="selectedVersion && hasUnsavedChanges">Selected version: credential verification required; exam date {{selectedVersion.requireExamDate ? 'required' : 'optional'}}; upload {{selectedVersion.requireCredentialUpload ? 'required' : 'optional'}}; {{selectedVersion.requireKra ? 'paired reviews valid for ' + selectedVersion.kraValidityDays + ' days' : 'reviews not required'}}.</p>
          </div>
          <p *ngIf="!detail.canChangeVersion">The assigned version is fixed because an offer has already been generated.</p>
          <button class="btn btn-primary mr-2" type="submit" [disabled]="!hasUnsavedChanges">Save changes</button>
          <button class="btn btn-outline-primary" type="button" [disabled]="hasUnsavedChanges || detail.status.paused" (click)="advance()">Try advancement</button>
          <p *ngIf="hasUnsavedChanges" class="mt-2">Save your changes before trying advancement.</p>
        </fieldset>
      </form>
      <p *ngIf="!detail.canManage">This transition is available for viewing only.</p>
      <h4 class="h5 mt-3">Offer history</h4>
      <p-table *ngIf="detail.offers.length; else noOffers" [value]="detail.offers" [responsive]="true">
        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="id">Offer <p-sortIcon field="id"></p-sortIcon></th>
            <th pSortableColumn="createdDate">Created <p-sortIcon field="createdDate"></p-sortIcon></th>
            <th pSortableColumn="validUntilDate">Valid through <p-sortIcon field="validUntilDate"></p-sortIcon></th>
            <th>Sent</th>
            <th>Status</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-offer>
            <tr>
              <td>#{{offer.id}}</td>
              <td>{{offer.createdDate | date:'short'}}</td>
              <td>{{offer.validUntilDate ? (offer.validUntilDate | date:'shortDate') : 'No expiration'}}</td>
              <td>{{offer.offerSentOn ? (offer.offerSentOn | date:'short') : offer.offerNotificationStartedOn ? 'Email delivery unconfirmed' : 'Not sent'}}</td>
              <td>{{offer.isDeleted ? 'Replaced / withdrawn' : offer.documentStatus || 'Not generated'}}</td>
            </tr>
        </ng-template>
      </p-table>
      <ng-template #noOffers><p>No offers have been generated.</p></ng-template>
    </div>
  </section>
</ng-container>
